import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Link, useNavigate } from "react-router-dom";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import StatusBadge from "@/components/StatusBadge";
import { Truck, Radio, Calendar, ChevronRight, Inbox as InboxIcon, Hash, Plus, Fuel, FileText, Zap } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Inbox() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState(user?.role === "mecanico" ? "mine" : "all");
  const [tasks, setTasks] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [starting, setStarting] = useState(null);

  useEffect(() => {
    const url = tab === "mine" ? "/tasks?mine=true" : "/tasks";
    api.get(url).then(({ data }) => setTasks(data));
  }, [tab]);

  const openPicker = async () => {
    setPickerOpen(true);
    try {
      const { data } = await api.get("/templates");
      setTemplates((data || []).filter((t) => t.permanent !== false && t.active !== false));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const startTemplate = async (t) => {
    setStarting(t.id);
    try {
      const { data } = await api.post("/tasks/quick-start", { checklist_template_id: t.id });
      toast.success(`Preenchimento iniciado · ${data.protocol_number}`);
      setPickerOpen(false);
      nav(`/inbox/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setStarting(null); }
  };

  const iconFor = (src) => src === "Fleetrun" ? Radio : src === "Manual" ? Zap : Calendar;
  const fuelIcon = (action) => action === "transfer_to_comboio" || action === "field_refuel" ? Fuel : FileText;

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto" data-testid="inbox-page">
      <PageHeader title="Minhas Inspeções" subtitle="Tarefas atribuídas para execução em campo"
        actions={
          <Button onClick={openPicker} className="gap-2" data-testid="new-fill-btn">
            <Plus className="w-4 h-4" /> Novo Preenchimento
          </Button>
        } />

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList>
          <TabsTrigger value="mine" data-testid="tab-mine">Minhas</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">Todas</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="space-y-3">
        {tasks.length === 0 && (
          <Card className="p-10 text-center">
            <InboxIcon className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
            <div className="font-semibold">Nenhuma inspeção nesta caixa</div>
            <p className="text-sm text-muted-foreground mt-1">
              Clique em <b>+ Novo Preenchimento</b> para iniciar um formulário permanente, ou aguarde um gatilho Fleetrun.
            </p>
          </Card>
        )}
        {tasks.map((t) => {
          const Icon = iconFor(t.trigger_source);
          const needsFix = t.status === "Pendente Correção";
          return (
            <Link key={t.id} to={`/inbox/${t.id}`} data-testid={`inbox-task-${t.id}`}>
              <Card className={`p-4 sm:p-5 transition-all active:scale-[0.99] ${needsFix ? "border-red-500/50 bg-red-500/5 hover:border-red-500" : "hover:border-primary"}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-11 h-11 rounded-md flex items-center justify-center flex-shrink-0 ${needsFix ? "bg-red-500/15" : "bg-primary/10"}`}>
                    <Icon className={`w-5 h-5 ${needsFix ? "text-red-500" : "text-primary"}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-1">
                      <div className="font-display font-bold text-base sm:text-lg leading-tight">{t.template?.title || "Checklist"}</div>
                      <StatusBadge status={t.status} />
                    </div>
                    {t.protocol_number && <div className="text-[10px] uppercase tracking-widest text-primary font-mono flex items-center gap-1"><Hash className="w-2.5 h-2.5" />{t.protocol_number}</div>}
                    <div className="flex items-center gap-2 flex-wrap text-sm text-muted-foreground mt-1">
                      {t.vehicle ? (
                        <>
                          <Truck className="w-3.5 h-3.5" />
                          <span className="font-mono">#{t.vehicle.fleet_number} · {t.vehicle.plate}</span>
                          <span>·</span>
                          <span>{t.vehicle.model}</span>
                        </>
                      ) : (
                        <span className="italic">Sem veículo vinculado</span>
                      )}
                    </div>
                    {needsFix && t.reject_reason && (
                      <div className="mt-2 text-xs p-2 rounded-md bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400" data-testid={`reject-reason-${t.id}`}>
                        <b>Motivo:</b> {t.reject_reason}
                      </div>
                    )}
                    {t.trigger_meta?.alert_type && (
                      <div className="text-xs mt-2 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-secondary">
                        <Radio className="w-3 h-3" /> {t.trigger_meta.alert_type}
                      </div>
                    )}
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground" />
                </div>
              </Card>
            </Link>
          );
        })}
      </div>

      {/* Picker Dialog */}
      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Iniciar Novo Preenchimento</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2 mb-2">Selecione um formulário permanente para iniciar agora.</p>
          <div className="space-y-2">
            {templates.length === 0 && (
              <div className="text-sm text-muted-foreground py-6 text-center">Nenhum formulário permanente disponível.</div>
            )}
            {templates.map((t) => {
              const FuelI = fuelIcon(t.fuel_action);
              return (
                <button key={t.id} type="button"
                  onClick={() => startTemplate(t)} disabled={starting === t.id}
                  data-testid={`pick-tpl-${t.id}`}
                  className="w-full text-left p-4 rounded-md border border-border hover:border-primary hover:bg-secondary transition-colors flex items-start gap-3 disabled:opacity-50">
                  <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <FuelI className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-display font-bold text-base leading-tight">{t.title}</div>
                      <div className="flex gap-1 flex-wrap justify-end">
                        {t.fuel_action === "transfer_to_comboio" && <Badge variant="outline" className="text-[10px] bg-primary/10 border-primary/30 text-primary">Transferência</Badge>}
                        {t.fuel_action === "field_refuel" && <Badge variant="outline" className="text-[10px] bg-primary/10 border-primary/30 text-primary">Abastecimento</Badge>}
                        <Badge variant="outline" className="text-[10px]">{t.trigger_type}</Badge>
                      </div>
                    </div>
                    {t.description && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{t.description}</div>}
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-2">
                      {t.structure_json?.length || 0} seções · {(t.structure_json || []).reduce((s, sec) => s + (sec.questions?.length || 0), 0)} perguntas
                    </div>
                  </div>
                  {starting === t.id && <div className="text-xs text-primary">Iniciando...</div>}
                </button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
